import { useState } from 'react';
import {
  CalendarBlankIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { AgentScheduleResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { useCan } from 'src/hooks/useCan';
import useOrganizationConfig from 'src/api/core/organization-config/useOrganizationConfig';
import { useAgentSchedules } from 'src/api/rpc/hooks/agent-schedules/useAgentSchedules';
import {
  agentScheduleToDraft,
  defaultAgentScheduleTimezone,
  newAgentScheduleDraft,
  type AgentScheduleDraft,
} from '../agentScheduleDraft';
import { scheduleNextRunLabel, scheduleRowIdentity } from '../agentScheduleRow';
import { AgentScheduleDialog } from './AgentScheduleDialog';
import { useAgentScheduleActions } from './useAgentScheduleActions';

function ScheduleRow({
  schedule,
  canEdit,
  onToggle,
  onEdit,
  onDelete,
  isToggling,
}: {
  schedule: AgentScheduleResponse;
  canEdit: boolean;
  onToggle: (isEnabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  isToggling: boolean;
}) {
  const gt = useGT();
  const { summary, description, identity } = scheduleRowIdentity(schedule);
  const nextRun = scheduleNextRunLabel(schedule, gt);

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <Switch
        checked={schedule.isEnabled}
        onCheckedChange={onToggle}
        disabled={!canEdit || isToggling}
        aria-label={
          schedule.isEnabled
            ? gt('Disable schedule {name}', {
                name: identity,
                context:
                  'accessible label for turning an agent schedule off; {name} is the schedule description or cron summary',
              })
            : gt('Enable schedule {name}', {
                name: identity,
                context:
                  'accessible label for turning an agent schedule on; {name} is the schedule description or cron summary',
              })
        }
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-medium text-stone-900">
            {description || summary}
          </span>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {nextRun}
          </span>
        </div>
        <div className="truncate text-[11.5px] text-muted-foreground">
          {description ? `${summary} · ` : ''}
          <code className="font-mono">{schedule.cronExpression}</code>
          {' · '}
          {schedule.timezone}
        </div>
      </div>
      {canEdit && (
        <ScheduleRowActions
          identity={identity}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </li>
  );
}

function ScheduleRowActions({
  identity,
  onEdit,
  onDelete,
}: {
  identity: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const gt = useGT();
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onEdit}
        aria-label={gt('Edit schedule {name}', {
          name: identity,
          context:
            'accessible label for the edit agent schedule button; {name} is the schedule description or cron summary',
        })}
      >
        <PencilSimpleIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:text-destructive"
        onClick={onDelete}
        aria-label={gt('Delete schedule {name}', {
          name: identity,
          context:
            'accessible label for the delete agent schedule button; {name} is the schedule description or cron summary',
        })}
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

function EmptySchedules({
  isLoading,
  canAdd,
}: {
  isLoading: boolean;
  canAdd: boolean;
}) {
  return (
    <p className="px-3 py-6 text-center text-sm text-muted-foreground">
      {isLoading ? (
        <T context="agent page schedules loading state">Loading schedules…</T>
      ) : canAdd ? (
        <T context="agent page schedules empty state, user can add one">
          No schedules yet. Add one to run this agent automatically.
        </T>
      ) : (
        <T context="agent page schedules empty state, read-only">
          This agent has no schedules.
        </T>
      )}
    </p>
  );
}

/**
 * The agent's run schedules: each row is a cron in a timezone that starts a
 * run of this agent. Only users who may run the agent can add, edit, toggle
 * or delete schedules; everyone who can see the page can read them.
 */
export function AgentSchedulesSection({
  agentDefinitionId,
}: {
  agentDefinitionId: string;
}) {
  const gt = useGT();
  const [canExecute] = useCan(Action.Execute, Subject.BackgroundAgent);
  const { data: schedules = [], isLoading } =
    useAgentSchedules(agentDefinitionId);
  const { data: orgConfig } = useOrganizationConfig();
  const defaultTimezone = defaultAgentScheduleTimezone(
    orgConfig?.organizationConfig?.timezone,
  );
  const actions = useAgentScheduleActions(agentDefinitionId);

  const [draft, setDraft] = useState<AgentScheduleDraft | null>(null);
  const [initialDraft, setInitialDraft] = useState<AgentScheduleDraft | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] =
    useState<AgentScheduleResponse | null>(null);

  function openDialog(next: AgentScheduleDraft) {
    setDraft(next);
    setInitialDraft(next);
  }

  function closeDialog() {
    setDraft(null);
    setInitialDraft(null);
  }

  async function handleSave() {
    if (draft && (await actions.save(draft))) closeDialog();
  }

  async function handleDelete() {
    if (pendingDelete && (await actions.destroy(pendingDelete))) {
      setPendingDelete(null);
    }
  }

  return (
    <section className="flex flex-col overflow-hidden rounded-lg border border-border bg-white">
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <CalendarBlankIcon className="size-4 text-stone-500" />
          <T context="agent page schedules heading">Schedules</T>
        </h2>
        {canExecute && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => openDialog(newAgentScheduleDraft(defaultTimezone))}
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            <T context="button to add a run schedule to an agent page">
              Add Schedule
            </T>
          </Button>
        )}
      </header>
      {schedules.length === 0 ? (
        <EmptySchedules isLoading={isLoading} canAdd={canExecute} />
      ) : (
        <ul className="divide-y divide-border">
          {schedules.map((schedule) => (
            <ScheduleRow
              key={schedule.id}
              schedule={schedule}
              canEdit={canExecute}
              isToggling={actions.togglingId === schedule.id}
              onToggle={(isEnabled) => void actions.toggle(schedule, isEnabled)}
              onEdit={() => openDialog(agentScheduleToDraft(schedule))}
              onDelete={() => setPendingDelete(schedule)}
            />
          ))}
        </ul>
      )}

      <AgentScheduleDialog
        draft={draft}
        initialDraft={initialDraft}
        onChange={setDraft}
        onClose={closeDialog}
        onSave={() => void handleSave()}
        isSaving={actions.isSaving}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void handleDelete()}
        closeOnConfirm={false}
        loading={actions.isDeleting}
        dangerous
        title={gt('Delete this schedule?', {
          context: 'confirm dialog title before deleting an agent run schedule',
        })}
        message={gt(
          'The agent will no longer run on this schedule. Runs it already started are kept.',
          {
            context:
              'confirm dialog body before deleting an agent run schedule',
          },
        )}
        confirmText={gt('Delete', {
          context: 'confirm button, delete an agent run schedule',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button in the delete agent schedule confirm dialog',
        })}
      />
    </section>
  );
}
